/* ---------------------------------------------------------------------------
   The ribbon.

   The app page's command surface, in the shape of Office on the web: a row of
   tabs, and under it the commands of the tab in front. The four applications
   ARE the tabs — Inspection, Rating, Scheduler, Monitoring — so moving between
   them is choosing a tab, and each tab's first group is that application's own
   screens, which is what the nav pane used to list. `File` goes back to the
   start page, `View` carries what belongs to no application: the theme
   and the ribbon's own layout, and `Help` carries the guide.

   TWO LAYOUTS OF THE SAME COMMANDS. Classic, the default, is the grouped ribbon
   with large buttons and a name under each group; simplified is one line, with
   the less-used commands reduced to their icon. Which one is a persisted
   habit (`ribbonMode`), switched from the corner of the tab row.

   THE TABS ARE ANCHORS, and that is for the same reason the nav pane's
   applications were: an app is an address (`#/scheduler/schedule/BR-…`, see
   src/core/router.ts), so a middle-click opens the programme in a second tab
   and a copied link names it. The commands are buttons, because they act.

   A COMMAND SOMEBODY MAY NOT USE IS DISABLED, NOT REMOVED — the rule the record
   and the works card already keep. An absent button reads as a broken
   application; a disabled one whose tooltip says why reads as an answer.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type IconPath } from '../core/dom.ts';
import { actions, can, selectedPhase, whyNot, type State } from '../core/store.ts';
import { accessLine, appAccess, phaseOf } from '../data/phase.ts';
import type { Capability } from '../data/roles.ts';
import { RIBBON_APPS, appForShell, type AppDef } from '../core/apps.ts';
import { appHref } from '../core/navigate.ts';
import { visibleNav } from './navpane.ts';
import { flyout, menuButton, menuItem, menuLabel } from './flyout.ts';
import { modelImportCommand } from '../screens/detail.ts';
import { findBridge, heroModel } from '../data/derive.ts';
import { startSimulation } from '../modeler/meshView.ts';
import { playedEntries } from '../data/buildSequence.ts';
import { t } from '../data/locale.ts';
import type { Bridge, Screen } from '../data/types.ts';
import { phaseChip, worksChip } from './phaseChip.ts';
import { shadedIcon } from './shadedIcon.ts';
import { goRating, rateStructure, ratingOpened, ratingScreen } from '../rating/mount.ts';
import { NAV_ICON, TITLE as RATING_TITLE } from '../rating/chrome.ts';
import { SCREENS as RATING_SCREENS } from '../rating/store.ts';

export interface Command {
  label: string;
  iconPath: IconPath;
  onClick: () => void;
  /** The one command a tab is mostly for. Filled with the accent. */
  primary?: boolean;
  /** A toggle that is on, or the screen that is showing. */
  on?: boolean;
  disabled?: boolean;
  title?: string;
  /** In the simplified layout, drawn as its icon alone (the label is its tooltip). */
  iconOnly?: boolean;
}

export interface Group {
  label: string;
  commands: Command[];
  /** No name drawn under it in the classic layout: an app's own screens need none. */
  unlabelled?: boolean;
}

/* ------------------------------ the commands ------------------------------ */

/**
 * An app's own screens: what the nav pane used to list, in its order.
 *
 * `omit` drops screens the ribbon does not offer as a tab command. The guide is
 * always dropped: it is on the Help tab. The keys printed beside the rest are
 * unchanged, because they are the pane's own.
 */
function screensGroup(s: Readonly<State>, omit: readonly Screen[] = []): Group {
  const skip: readonly Screen[] = [...omit, 'guide'];
  return {
    label: t('Go to'),
    unlabelled: true,
    commands: visibleNav(s).filter((entry) => !skip.includes(entry.screen)).map((entry) => ({
      label: t(entry.label),
      iconPath: entry.iconPath,
      on: entry.screen === s.screen,
      title: `${t(entry.label)} (${entry.key})`,
      onClick: () => actions.go(entry.screen),
    })),
  };
}

/** Somebody may do this, or here is why not, as a Command's two fields. */
function gate(
  s: Readonly<State>,
  ok: boolean,
  what: string,
  title: string,
  capability?: Capability,
): Pick<Command, 'disabled' | 'title'> {
  /* The capability, where given, lets the tooltip say the structure's state is
     why (LC-13) rather than blaming the roles for it. */
  return ok ? { title } : { disabled: true, title: whyNot(s, what, capability) };
}

function inspectionGroups(s: Readonly<State>): Group[] {
  const b = findBridge(s.selectedId);
  const none = b === null;
  const on = b ? ` ${b.name}` : '';
  /* The inventory sheet is a screen of the app, but it is drawn after Log
     inspection, beside the other sheet, so it is lifted out of Go to. */
  const inventory = screensGroup(s, visibleNav(s).map((e) => e.screen).filter((sc) => sc !== 'report')).commands;
  return [
    /* No Register and no Network: on a desk both are the start page (see
       deskSendsHome), and the title bar's back arrow and search are the way there.
       No sheets either: they are in the Record group below. */
    screensGroup(s, ['list', 'network', 'sheet', 'report']),
    {
      label: t('Record'),
      commands: [
        {
          label: t('Log overall inspection'),
          iconPath: ICONS.clipboard,
          primary: true,
          ...gate(s, !none && can(s, 'inspection.log'), 'Logging an inspection', `Add an inspection entry to${on}`, 'inspection.log'),
          onClick: () => b && actions.openAddInspection(b.id),
        },
        ...inventory,
        /* NO LOG DEFECT. It is on the record's 3D toolbar, where it places the
           defect at the point clicked on the model (src/modeler/meshView.ts). */
        /* NO DISTRICT RATES. They and the wording of recommended work are
           reference tables, the administrator's, at the foot of Bridge's
           explorer (src/screens/detail.ts, referenceTables). */
        {
          label: t('Inspection summary sheet'),
          iconPath: ICONS.report,
          on: s.screen === 'sheet',
          ...(none ? { disabled: true, title: 'There is no structure in the register' } : { title: `The inspection summary sheet of${on}` }),
          onClick: () => actions.go('sheet'),
        },
        /* NO IMPORT OR DELETE OF THE 3D MODEL: they are on the structure's
           dashboard on the start page (src/screens/bridgeDashboard.ts). No
           Import rows, which is on the register, and no Print. */
      ],
    },
  ];
}

/* The Scheduler's own screens, like every app's, and Simulate beside the 3D
   model. No Programme group: Gantt, table and calendar are the switch in the
   Programme panel's head (src/screens/detail.ts, scheduleLayoutSwitch). */
function schedulerGroups(s: Readonly<State>): Group[] {
  /* No Register: the title bar's search is the way to it, as in Bridge Inspection. */
  const screens = screensGroup(s, ['list']);
  return [
    { ...screens, commands: [...screens.commands, simulateCommand(s, 'programme')] },
    /* No Baseline group: taking, revising and accepting the baseline are on the
       Baseline page, which the explorer opens — src/screens/baselinePage.ts. */
  ];
}

/**
 * The construction played on the model, day by day: opens the 3D model and
 * starts the playback there, the canvas toolbar's Simulate pressed for you.
 * The Scheduler plays the programme — the accepted baseline only, a proposed
 * one is not the programme (LC-40); see planProgramme in
 * src/data/buildSequence.ts. Reporting plays the record — the accepted entries
 * only, as the canvas does (playedEntries, LC-52); see planBuild.
 */
function simulateCommand(s: Readonly<State>, source: 'programme' | 'record'): Command {
  const b = findBridge(s.selectedId);
  const why =
    b === null
      ? 'There is no structure in the register'
      : !heroModel(s.models.models)?.hasGeometry
        ? `${b.name} has no 3D model to simulate on`
        : source === 'programme'
          ? s.baseline?.bridgeId !== b.id
            ? 'There is no accepted programme for this structure to simulate'
            : null
          : s.progressRead === 'read' && playedEntries(s.progress.filter((e) => e.bridgeId === b.id)).length === 0
            ? 'No accepted work has been recorded against this structure to play back'
            : null;
  return {
    label: t('Simulate'),
    iconPath: ICONS.simulate,
    ...(why === null
      ? {
          title:
            source === 'programme'
              ? 'Simulate the construction on the 3D model, day by day, as the programme plans it'
              : 'Play the construction record back on the 3D model, day by day, as the site recorded it',
        }
      : { disabled: true, title: why }),
    onClick: () => {
      if (s.screen !== 'detail') actions.go('detail');
      startSimulation();
    },
  };
}

function monitoringGroups(s: Readonly<State>): Group[] {
  const b = findBridge(s.selectedId);
  const ok = b !== null && can(s, 'progress.record');
  const g = (what: string, title: string) => gate(s, ok, what, title, 'progress.record');
  const id = b?.id ?? '';
  const at = b ? ` on ${b.name}` : '';
  /* No Register, as in Bridge Inspection: the title bar's search is the way
     to it, and the structure in hand is already in the side panels. No site
     record, whose pages are the explorer's, and no period report, which the
     explorer opens as well. No Print. */
  const screens = screensGroup(s, ['list', 'site', 'works']);
  return [
    /* Simulate beside the 3D model, as in the Scheduler, playing the record. */
    { ...screens, commands: [...screens.commands, simulateCommand(s, 'record')] },
    {
      label: t('Record'),
      commands: [
        {
          label: t('Record work'),
          iconPath: ICONS.plus,
          primary: true,
          ...g('Recording work', `Record something done${at}`),
          onClick: () => actions.openRecordProgress(id),
        },
        {
          label: t('Incident'),
          iconPath: ICONS.incident,
          ...g('Reporting an incident', 'Report a near miss, an injury or an environmental event'),
          onClick: () => actions.openIncident(id),
        },
        {
          label: t('Document'),
          iconPath: ICONS.folder,
          ...g('Filing a document', 'File a test certificate, a delivery note or a method statement'),
          onClick: () => actions.openDocument(id),
        },
      ],
    },
  ];
}

/**
 * The structure's 3D model, in the content panel. Every application has it —
 * see src/core/apps.ts — and this is the command for Bridge Rating, whose ribbon
 * does not draw it from `screensGroup`.
 */
function modelCommand(s: Readonly<State>, on = s.screen === 'detail'): Command {
  return {
    label: t('3D model'),
    iconPath: ICONS.cube,
    on,
    title: 'The 3D model of the structure in hand',
    onClick: () => {
      /* Bridge Rating's ratings on record take the model's place; the model
         comes back beside the rating form. */
      if (s.detailView === 'ratingHistory') actions.showDetailView('rating');
      actions.go('detail');
    },
  };
}

/* Bridge: the model beside what the structure is. Its pages — Bridge info,
   State, the reference tables — are the explorer's. No Print: nothing on
   Bridge is laid out for it. */
function bridgeGroups(s: Readonly<State>): Group[] {
  const add = modelImportCommand(s);
  const may = can(s, 'model.manage');
  return [
    screensGroup(s),
    {
      label: t('Model'),
      commands: [
        add === null
          ? {
              label: t('Import Bridge Model'),
              iconPath: ICONS.import,
              disabled: true,
              title: 'There is no structure in the register to import onto',
              onClick: () => {},
            }
          : {
              label: add.busy ? add.label : t('Import Bridge Model'),
              iconPath: ICONS.import,
              disabled: add.busy || !may,
              title: add.busy || may ? add.title : whyNot(s, 'Importing a 3D model', 'model.manage'),
              onClick: add.run,
            },
      ],
    },
  ];
}

function ratingGroups(s: Readonly<State>): Group[] {
  const here = ratingScreen();
  const onModel = s.screen === 'detail';
  const b = findBridge(s.selectedId);
  /* Off the model and onto Bridge Rating's own screens, whose address names
     the screen (LC-31) — so the address is told the screen first, and moving
     is one history entry rather than two. See withApp in src/core/router.ts. */
  const leaveModel = () => {
    actions.ratingMoved(ratingScreen(), ratingOpened());
    if (onModel) actions.go('dashboard');
  };
  return [
    {
      label: t('Go to'),
      unlabelled: true,
      commands: [
        modelCommand(s, onModel && s.detailView !== 'rating' && s.detailView !== 'ratingHistory'),
        {
          label: t('Rate this structure'),
          iconPath: ICONS[NAV_ICON.rate],
          on: onModel && s.detailView === 'rating',
          ...(b === null
            ? { disabled: true, title: 'There is no structure in the register to rate' }
            : appAccess(phaseOf(b), 'rating').mode === 'hidden'
              ? { disabled: true, title: accessLine(phaseOf(b), 'rating') ?? '' }
              : { title: `Rate ${b.name}` }),
          /* The form in the property panel, the 3D model in the content panel. */
          onClick: () => {
            if (b === null) return;
            rateStructure(b.id);
            actions.showDetailView('rating');
            if (!onModel) actions.go('detail');
          },
        },
        /* Ratings only: Structures and Method are not commands of the ribbon —
           Method's Print and export are on its panel's header. */
        ...RATING_SCREENS.filter((screen) => screen === 'ratings').map((screen) => ({
          label: RATING_TITLE[screen],
          iconPath: ICONS[NAV_ICON[screen]],
          on: !onModel && screen === here,
          title: RATING_TITLE[screen],
          onClick: () => {
            goRating(screen);
            leaveModel();
          },
        })),
      ],
    },
  ];
}

function viewGroups(s: Readonly<State>): Group[] {
  const groups: Group[] = [
    {
      label: t('Theme'),
      commands: [
        {
          label: t('Dark theme'),
          iconPath: ICONS.moon,
          on: s.theme === 'dark',
          onClick: () => actions.toggleTheme(),
        },
      ],
    },
    {
      label: t('Ribbon'),
      commands: [
        {
          label: t('Classic ribbon'),
          iconPath: ICONS.ribbon,
          on: s.ribbonMode === 'classic',
          title: 'Large buttons in named groups, instead of one line of commands',
          onClick: () => actions.toggleRibbonMode(),
        },
      ],
    },
  ];
  return groups;
}

/* The guide, docked on the right of whichever app you are in — the same as the
   title bar's help button. */
function helpGroups(s: Readonly<State>): Group[] {
  return [
    {
      label: t('Help'),
      commands: [
        {
          label: t('Guide'),
          iconPath: ICONS.book,
          on: s.guideOpen,
          title: 'How to use Bridge Watch, beside the screen you are on',
          onClick: () => actions.toggleGuide(),
        },
      ],
    },
  ];
}

/** The groups for the tab in front. */
export function ribbonGroups(s: Readonly<State>, app: AppDef): Group[] {
  if (s.ribbonTab === 'view') return viewGroups(s);
  if (s.ribbonTab === 'help') return helpGroups(s);
  switch (app.id) {
    case 'structure':
      return bridgeGroups(s);
    case 'inspection':
      return inspectionGroups(s);
    case 'rating':
      return ratingGroups(s);
    case 'scheduler':
      return schedulerGroups(s);
    case 'monitoring':
      return monitoringGroups(s);
    case 'home':
      return [];
  }
}

/* ------------------------------ drawing ------------------------------ */

function commandButton(c: Command, classic: boolean): HTMLButtonElement {
  const bare = !classic && c.iconOnly === true;
  return h(
    'button',
    {
      type: 'button',
      class: [
        'rbtn',
        classic && 'rbtn--big',
        bare && 'rbtn--icon',
        c.primary && 'rbtn--primary',
        c.on && 'rbtn--on',
      ],
      disabled: c.disabled || undefined,
      'aria-pressed': c.on === undefined ? undefined : c.on ? 'true' : 'false',
      'aria-label': bare ? c.label : undefined,
      title: c.title ?? c.label,
      onclick: c.onClick,
    },
    shadedIcon(c.iconPath, classic ? 32 : 16),
    bare ? null : h('span', { class: 'rbtn__label', text: c.label }),
  );
}

function tabs(s: Readonly<State>, app: AppDef): HTMLElement {
  const viewing = s.ribbonTab === 'view';
  const helping = s.ribbonTab === 'help';
  return h(
    'div',
    { class: 'ribbon__tabs', role: 'tablist', 'aria-label': 'Ribbon' },
    h('a', {
      class: 'rtab rtab--file',
      href: '#/',
      title: 'Back to the start page',
      text: t('File'),
    }),
    ...RIBBON_APPS.map((a) => {
      const on = s.ribbonTab === 'app' && a.id === app.id;
      const p = selectedPhase(s);
      const mode = p === null || a.id === 'home' ? 'open' : appAccess(p, a.id).mode;
      /* An application the structure's state hides is a tab that cannot be
         pressed (LC-13): no address, and the tooltip says why and when it
         opens. Disabled rather than removed, like every other command here. */
      if (mode === 'hidden' && p !== null && a.id !== 'home') {
        return h(
          'span',
          {
            class: ['rtab', on && 'rtab--on', 'rtab--hidden'],
            role: 'tab',
            'aria-selected': on ? 'true' : 'false',
            'aria-disabled': 'true',
            title: accessLine(p, a.id) ?? a.blurb,
          },
          h('span', { text: t(a.short) }),
        );
      }
      return h(
        'a',
        {
          class: ['rtab', on && 'rtab--on', mode !== 'open' && `rtab--${mode}`],
          role: 'tab',
          'aria-selected': on ? 'true' : 'false',
          href: appHref(s, a.id),
          title: mode === 'open' || p === null || a.id === 'home' ? a.blurb : (accessLine(p, a.id) ?? a.blurb),
          /* No preventDefault: the address carries the change of app. This only
             brings the app's own commands back in front of the View tab. */
          onclick: () => actions.setRibbonTab('app'),
        },
        h('span', { text: t(a.short) }),
      );
    }),
    h(
      'button',
      {
        type: 'button',
        class: ['rtab', viewing && 'rtab--on'],
        role: 'tab',
        'aria-selected': viewing ? 'true' : 'false',
        onclick: () => actions.setRibbonTab(viewing ? 'app' : 'view'),
      },
      h('span', { text: t('View') }),
    ),
    h(
      'button',
      {
        type: 'button',
        class: ['rtab', helping && 'rtab--on'],
        role: 'tab',
        'aria-selected': helping ? 'true' : 'false',
        onclick: () => actions.setRibbonTab(helping ? 'app' : 'help'),
      },
      h('span', { text: t('Help') }),
    ),
    h('span', { class: 'spacer' }),
    /* The layout in force, named, with the choice under it — the corner of the
       tab row where Office keeps the same menu. */
    h(
      'button',
      {
        type: 'button',
        class: 'ribbon__mode',
        title: 'Choose how the ribbon is laid out',
        ...menuButton(s, 'ribbon'),
      },
      h('span', { text: s.ribbonMode === 'simplified' ? t('Simplified ribbon') : t('Classic ribbon') }),
      icon(ICONS.chevronDown, { size: 12, width: 2 }),
    ),
    flyout(
      s,
      'ribbon',
      { label: 'Ribbon layout', width: 264, align: 'end' },
      h(
        'div',
        { class: 'flyout__list' },
        menuLabel(t('Ribbon')),
        menuItem({
          label: t('Classic ribbon'),
          sub: 'Large buttons with group labels',
          iconPath: ICONS.ribbon,
          checked: s.ribbonMode === 'classic',
          onClick: () => actions.setRibbonMode('classic'),
        }),
        menuItem({
          label: t('Simplified ribbon'),
          sub: 'One row of commands',
          iconPath: ICONS.ribbonLine,
          checked: s.ribbonMode === 'simplified',
          onClick: () => actions.setRibbonMode('simplified'),
        }),
      ),
    ),
  );
}

/** `record` is the structure whose explorer is open, if one is: its phase
    chip sits at the far end of the command bar (LC-10). */
export function ribbon(s: Readonly<State>, record: Bridge | null = null): HTMLElement {
  const app = appForShell(s.shell);
  const classic = s.ribbonMode === 'classic';
  const groups = ribbonGroups(s, app);

  return h(
    'div',
    { class: 'ribbon' },
    tabs(s, app),
    h(
      'div',
      {
        class: ['ribbon__bar', classic && 'ribbon__bar--classic'],
        role: 'toolbar',
        'aria-label':
          s.ribbonTab === 'view' ? 'View' : s.ribbonTab === 'help' ? 'Help' : `${app.label} commands`,
      },
      ...groups.flatMap((g, i) => [
        i > 0 ? h('span', { class: 'ribbon__sep', 'aria-hidden': 'true' }) : null,
        h(
          'div',
          { class: 'rgroup', role: 'group', 'aria-label': g.label },
          h('div', { class: 'rgroup__cmds' }, ...g.commands.map((c) => commandButton(c, classic))),
          classic && !g.unlabelled ? h('div', { class: 'rgroup__label', text: g.label }) : null,
        ),
      ]),
      record
        ? h('span', { class: 'ribbon__state' }, phaseChip(record, true), worksChip(record))
        : null,
    ),
  );
}
